import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { PlusIcon, TrashIcon, Share1Icon } from '@radix-ui/react-icons';
import Layout from '../components/common/Layout';
import { Button } from '../components/common/Button';
import { Card } from '../components/common/Card';
import ConfirmModalAlert from '../components/common/ConfirmModalAlert';
import { useToast } from '../hooks/useToast';
import { createMindMap, deleteMindMap, getMindMaps } from '../api/mindmap';
import { NODE_COLORS } from '../components/mindmap/palette';
import '../components/mindmap/MindMap.css';

const NEW_MAP_TITLE = 'My mind map';

const colorFor = (id) => NODE_COLORS[[...id].reduce((sum, char) => sum + char.charCodeAt(0), 0) % (NODE_COLORS.length - 1)].key;

const MapPreview = ({ color }) => (
  <svg viewBox="0 0 120 56" className="h-14 w-full" aria-hidden="true">
    <g className={`mm-c-${color}`}>
      <g className="mm-edge" style={{ strokeWidth: 2 }}>
        <path d="M60 28 C 42 28, 42 12, 26 12" />
        <path d="M60 28 C 42 28, 42 44, 26 44" />
        <path d="M60 28 C 78 28, 78 20, 94 20" />
        <path d="M60 28 C 78 28, 78 40, 94 40" />
      </g>
      <g fill="var(--mm-solid)" className="mm-swatch">
        <rect x="46" y="20" width="28" height="16" rx="6" />
      </g>
      <g fill="var(--mm-bg)" stroke="var(--mm-border)" strokeWidth="1.5" className="mm-swatch">
        <rect x="6" y="6" width="20" height="12" rx="5" />
        <rect x="6" y="38" width="20" height="12" rx="5" />
        <rect x="94" y="14" width="20" height="12" rx="5" />
        <rect x="94" y="34" width="20" height="12" rx="5" />
      </g>
    </g>
  </svg>
);

export default function MindMapsPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [mindMaps, setMindMaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [pendingDelete, setPendingDelete] = useState(null);

  useEffect(() => {
    getMindMaps()
      .then(setMindMaps)
      .catch((error) => toast({ title: 'Error', description: error.message || 'Failed to load mind maps', type: 'error' }))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreate = async () => {
    setCreating(true);
    try {
      const created = await createMindMap(NEW_MAP_TITLE);
      navigate(`/mindmaps/${created.id}?new=1`);
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Failed to create mind map', type: 'error' });
      setCreating(false);
    }
  };

  const handleDelete = async () => {
    const target = pendingDelete;
    setPendingDelete(null);
    try {
      await deleteMindMap(target.id);
      setMindMaps((current) => current.filter((map) => map.id !== target.id));
      toast({ title: 'Deleted', description: `"${target.title}" was deleted`, type: 'success' });
    } catch (error) {
      toast({ title: 'Error', description: error.message || 'Failed to delete mind map', type: 'error' });
    }
  };

  return (
    <Layout>
      <div className="space-y-8">
        <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Mind maps</h1>
            <p className="text-muted-foreground">Map out your goals, milestones, learnings and ideas</p>
          </div>
          <Button onClick={handleCreate} disabled={creating}>
            <PlusIcon className="mr-2 h-4 w-4" />
            New mind map
          </Button>
        </div>

        {loading ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" aria-label="Loading mind maps">
            {[0, 1, 2].map((placeholder) => (
              <div key={placeholder} className="h-40 animate-pulse rounded-lg bg-muted" />
            ))}
          </div>
        ) : mindMaps.length === 0 ? (
          <div className="flex flex-col items-center gap-4 rounded-2xl border border-dashed border-border px-6 py-16 text-center">
            <div className="rounded-full bg-primary/10 p-4 text-primary">
              <Share1Icon className="h-6 w-6" />
            </div>
            <div className="space-y-1">
              <h2 className="text-lg font-semibold">Start visualizing your future</h2>
              <p className="max-w-sm text-sm text-muted-foreground">
                Put a goal in the middle, then branch out into milestones, learnings and findings.
              </p>
            </div>
            <Button onClick={handleCreate} disabled={creating}>
              <PlusIcon className="mr-2 h-4 w-4" />
              Create your first mind map
            </Button>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {mindMaps.map((map) => (
              <Card
                key={map.id}
                role="link"
                tabIndex={0}
                onClick={() => navigate(`/mindmaps/${map.id}`)}
                onKeyDown={(event) => event.key === 'Enter' && navigate(`/mindmaps/${map.id}`)}
                className="hover-card group cursor-pointer space-y-3 p-5"
              >
                <MapPreview color={colorFor(map.id)} />
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <h3 className="truncate font-semibold">{map.title}</h3>
                    <p className="text-sm text-muted-foreground">
                      {map.nodeCount} {map.nodeCount === 1 ? 'idea' : 'ideas'} · edited{' '}
                      {formatDistanceToNow(new Date(map.updatedAt), { addSuffix: true })}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`Delete ${map.title}`}
                    className="-mr-2 -mt-1 h-9 w-9 shrink-0 text-muted-foreground hover:text-destructive"
                    onClick={(event) => {
                      event.stopPropagation();
                      setPendingDelete(map);
                    }}
                    onKeyDown={(event) => event.stopPropagation()}
                  >
                    <TrashIcon className="h-4 w-4" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      <ConfirmModalAlert
        isOpen={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        onConfirm={handleDelete}
        title="Delete this mind map?"
        message={pendingDelete ? `"${pendingDelete.title}" and all of its ideas will be deleted.` : ''}
        confirmText="Delete"
      />
    </Layout>
  );
}
